import { useState, useEffect } from "react";
import { exportAPI } from "../../lib/api";
import { toast } from "sonner";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);



const typeColor = t => ({ PDF: "#EF4444", Markdown: "#22C55E", JSON: "#F59E0B", CSV: "#8B5CF6" }[t] || TEXT_MUTED);
const shareColor = s => ({ Team: ACCENT, Org: "#8B5CF6", Private: TEXT_DIM }[s] || TEXT_DIM);

const timeAgo = (dateStr) => {
  if (!dateStr) return "N/A";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

/* ── Template Card ── */
const TemplateCard = ({ t, onEdit }) => (
  <div style={{
    background: SURFACE, border: `1px solid ${t.active ? BORDER : BORDER}`,
    borderRadius: 10, padding: "16px 18px", opacity: t.active ? 1 : 0.6,
  }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{t.name}</span>
          <Badge label={`v${t.version}`} color={TEXT_DIM} />
          {t.locked && <Badge label="LOCKED" color="#F59E0B" />}
          {!t.active && <Badge label="INACTIVE" color={TEXT_DIM} />}
          <Badge label={t.type.toUpperCase()} color={t.type === "output" ? ACCENT : "#22C55E"} />
        </div>
        <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
          By {t.createdBy} · Updated {t.updated}
        </div>
      </div>
    </div>

    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 6, letterSpacing: "0.06em" }}>INCLUDED MODULES</div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {t.modules.map(m => (
          <span key={m} style={{ fontSize: 11, color: TEXT_MUTED, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 8px" }}>{m}</span>
        ))}
      </div>
    </div>

    <div style={{ display: "flex", gap: 8 }}>
      <button onClick={() => onEdit(t)} style={{
        background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`,
        borderRadius: 7, padding: "7px 14px", fontSize: 12, color: ACCENT,
        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
      }}>{t.locked ? "View" : "Edit"}</button>
      <button style={{
        background: "transparent", border: `1px solid ${BORDER}`,
        borderRadius: 7, padding: "7px 14px", fontSize: 12, color: TEXT_DIM,
        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
      }}>Duplicate</button>
      <button style={{
        background: "transparent", border: `1px solid ${BORDER}`,
        borderRadius: 7, padding: "7px 14px", fontSize: 12, color: TEXT_DIM,
        cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
        marginLeft: "auto",
      }}>{t.active ? "Deactivate" : "Activate"}</button>
    </div>
  </div>
);

/* ── Template Editor ── */
const TemplateEditor = ({ template, onClose }) => {
  const ALL_MODULES = ["Overview", "Payments + BNPL", "Signals", "Classification", "Hypotheses", "Buying Reality", "MEDDICC", "Traffic", "Articles", "Contacts", "Brief"];
  const [selected, setSelected] = useState(new Set(template?.modules || []));
  const [name, setName] = useState(template?.name || "");
  const [saved, setSaved] = useState(false);

  const toggle = m => setSelected(s => {
    const n = new Set(s);
    n.has(m) ? n.delete(m) : n.add(m);
    return n;
  });

  const save = () => { setSaved(true); setTimeout(() => { setSaved(false); onClose(); }, 1200); };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center",
      backdropFilter: "blur(4px)",
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 520, background: SURFACE, border: `1px solid ${BORDER}`,
        borderRadius: 14, overflow: "hidden",
        boxShadow: "0 40px 80px rgba(0,0,0,0.6)",
        animation: "fadeUp 0.2s ease",
      }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>
            {template ? "Edit Template" : "New Template"}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_MUTED }}>
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div style={{ padding: "20px 22px" }}>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, color: "#9CA3AF", display: "block", marginBottom: 6 }}>Template Name</label>
            <input value={name} onChange={e => setName(e.target.value)} style={{
              width: "100%", background: SURFACE_2, border: `1px solid ${BORDER}`,
              borderRadius: 8, padding: "10px 14px", fontSize: 14, color: TEXT_PRIMARY,
              outline: "none", fontFamily: "'DM Sans', sans-serif",
            }}
              onFocus={e => e.target.style.borderColor = ACCENT}
              onBlur={e => e.target.style.borderColor = BORDER}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, color: "#9CA3AF", display: "block", marginBottom: 8 }}>Included Modules</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
              {ALL_MODULES.map(m => (
                <button key={m} onClick={() => toggle(m)} style={{
                  display: "flex", alignItems: "center", gap: 9,
                  background: selected.has(m) ? `${ACCENT}10` : SURFACE_2,
                  border: `1px solid ${selected.has(m) ? ACCENT + "44" : BORDER}`,
                  borderRadius: 7, padding: "9px 12px", cursor: "pointer", textAlign: "left",
                }}>
                  <div style={{
                    width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                    background: selected.has(m) ? ACCENT : SURFACE,
                    border: `1.5px solid ${selected.has(m) ? ACCENT : BORDER}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {selected.has(m) && <svg width={8} height={8} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}><polyline points="20 6 9 17 4 12" /></svg>}
                  </div>
                  <span style={{ fontSize: 12, color: selected.has(m) ? TEXT_PRIMARY : TEXT_MUTED }}>{m}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{
              flex: 1, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 8,
              padding: "11px", fontSize: 13, color: TEXT_MUTED, cursor: "pointer",
            }}>Cancel</button>
            <button onClick={save} style={{
              flex: 2, background: saved ? "#052E16" : ACCENT,
              color: saved ? "#22C55E" : "#fff", border: `1px solid ${saved ? "#14532D" : "transparent"}`,
              borderRadius: 8, padding: "11px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s",
            }}>{saved ? "✓ Saved" : "Save Template"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── ROOT ── */
export default function InferenceTemplatesExports({ workspaceId, onViewAnalysis }) {
  const [activeTab, setActiveTab] = useState("templates");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [exports, setExports] = useState([]);
  const [exportsLoading, setExportsLoading] = useState(true);
  const [exportsLoadingMore, setExportsLoadingMore] = useState(false);
  const [exportsHasMore, setExportsHasMore] = useState(false);
  const EXPORTS_PAGE_SIZE = 20;
  const [briefSearch, setBriefSearch] = useState("");
  const [briefFormatFilter, setBriefFormatFilter] = useState("All");
  const [templates, setTemplates] = useState([]);

  // Mapper shared between initial-load + load-more.
  const mapExport = (e, idx) => ({
    id: e.export_id || `${idx}-${e.created_at || ""}`,
    export_id: e.export_id,
    analysis_id: e.analysis_id,
    company: e.company || e.company_name || e.analysis_id || "Unknown",
    type: (e.format || "PDF").toUpperCase() === "MARKDOWN"
      ? "Markdown"
      : (e.format || "PDF").charAt(0).toUpperCase() + (e.format || "pdf").slice(1).toLowerCase(),
    modules: e.modules || ["Overview"],
    sharedWith: e.shared_with || "Private",
    size: e.size || "N/A",
    createdBy: e.created_by || "You",
    ts: e.created_at ? timeAgo(e.created_at) : "N/A",
    createdAtRaw: e.created_at || null,
  });

  useEffect(() => {
    const fetchExports = async () => {
      setExportsLoading(true);
      try {
        const res = await exportAPI.list({ skip: 0, limit: EXPORTS_PAGE_SIZE });
        const rows = res.data || [];
        setExports(rows.map(mapExport));
        setExportsHasMore(rows.length === EXPORTS_PAGE_SIZE);
      } catch (err) {
        console.error("Failed to fetch exports:", err);
        toast.error("Could not load exports.");
      } finally {
        setExportsLoading(false);
      }
    };
    fetchExports();
  }, []);

  const loadMoreExports = async () => {
    if (exportsLoadingMore || !exportsHasMore) return;
    setExportsLoadingMore(true);
    try {
      const res = await exportAPI.list({ skip: exports.length, limit: EXPORTS_PAGE_SIZE });
      const rows = res.data || [];
      // Filter out any duplicates by export_id just in case rows were added/deleted between pages.
      const known = new Set(exports.map(e => e.export_id).filter(Boolean));
      const newRows = rows.filter(r => !known.has(r.export_id)).map(mapExport);
      setExports(prev => [...prev, ...newRows]);
      setExportsHasMore(rows.length === EXPORTS_PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load more exports:", err);
      toast.error("Could not load more exports.");
    } finally {
      setExportsLoadingMore(false);
    }
  };

  // Download an export's content via GET /exports/{id} → blob → save.
  const handleDownloadExport = async (e) => {
    if (!e?.export_id) {
      toast.error("Export id missing — cannot download.");
      return;
    }
    try {
      const res = await exportAPI.get(e.export_id);
      const doc = res.data || {};
      const fmt = (doc.format || "MARKDOWN").toUpperCase();
      const ext = fmt === "JSON" ? "json" : fmt === "CSV" ? "csv" : fmt === "PDF" ? "md" : "md";
      const mime = fmt === "JSON" ? "application/json" : fmt === "CSV" ? "text/csv" : "text/markdown";
      const safeCompany = (doc.company || e.company || "export").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      const blob = new Blob([doc.content || ""], { type: `${mime};charset=utf-8` });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeCompany}_${e.export_id}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download export failed:", err);
      toast.error("Could not download export.");
    }
  };

  const handleReExport = (e) => {
    if (!e?.analysis_id) {
      toast.error("Cannot re-export — analysis id missing.");
      return;
    }
    if (onViewAnalysis) {
      onViewAnalysis(e.analysis_id);
    }
  };

  const TABS = ["templates", "exports", "briefs"];

  // Brief Library: client-side filtering — no extra API calls.
  const briefFormatOptions = ["All", "PDF", "Markdown", "JSON"];
  const briefFiltersActive = briefSearch.trim().length > 0 || briefFormatFilter !== "All";
  const filteredBriefs = exports.filter(b => {
    if (briefSearch.trim()) {
      if (!String(b.company || "").toLowerCase().includes(briefSearch.trim().toLowerCase())) return false;
    }
    if (briefFormatFilter !== "All") {
      // b.type was titlecased in the fetch mapper ("Markdown" / "Pdf" / "Json")
      if (String(b.type || "").toLowerCase() !== briefFormatFilter.toLowerCase()) return false;
    }
    return true;
  });
  const clearBriefFilters = () => { setBriefSearch(""); setBriefFormatFilter("All"); };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
        input::placeholder { color: #374151; }
        button { outline: none; }
      `}</style>

      {/* Tab bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 28px 0" }}>
        <div style={{ display: "flex", gap: 4 }}>
          {TABS.map(t => (
            <button key={t} data-testid={`exports-tab-${t}`} onClick={() => setActiveTab(t)} style={{
              fontSize: 13, padding: "6px 14px", borderRadius: 6, cursor: "pointer",
              border: `1px solid ${activeTab === t ? ACCENT : "transparent"}`,
              background: activeTab === t ? `${ACCENT}18` : "transparent",
              color: activeTab === t ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
              textTransform: "capitalize", fontWeight: activeTab === t ? 600 : 400,
            }}>{t === "briefs" ? "Brief Library" : t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>
        {activeTab === "templates" && (
          <button onClick={() => { setEditingTemplate(null); setShowEditor(true); }} style={{
            background: ACCENT, color: "#fff", border: "none", borderRadius: 7,
            padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 6,
          }}>
            + New Template
          </button>
        )}
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px" }}>

        {/* TEMPLATES */}
        {activeTab === "templates" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 4 }}>Output Templates</h2>
              <p style={{ fontSize: 13, color: TEXT_MUTED }}>Templates define which modules appear in exported briefs. Each has a version history and can be locked to prevent editing.</p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {templates.length === 0 ? (
                <div data-testid="templates-empty-state" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "60px 0", border: `1px dashed ${BORDER}`, borderRadius: 8 }}>
                  <p style={{ fontSize: 14, color: TEXT_MUTED }}>No custom templates yet.</p>
                </div>
              ) : (templates.map(t => (
                <TemplateCard key={t.id} t={t} onEdit={tmpl => { setEditingTemplate(tmpl); setShowEditor(true); }} />
              )))}
            </div>
          </div>
        )}

        {/* EXPORTS */}
        {activeTab === "exports" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 4 }}>Export Center</h2>
              <p style={{ fontSize: 13, color: TEXT_MUTED }}>All generated exports. Download, share, or manage access permissions.</p>
            </div>
            {exports.length === 0 ? (
              <div data-testid="exports-empty-state" style={{ textAlign: "center", padding: "80px 0", border: `1px dashed ${BORDER}`, borderRadius: 8 }}>
                <p style={{ fontSize: 15, color: TEXT_PRIMARY, marginBottom: 8 }}>No exports yet.</p>
                <p style={{ fontSize: 13, color: TEXT_MUTED }}>Export a Brief from any analysis to see it here.</p>
              </div>
            ) : (
            <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: SURFACE_2 }}>
                    {["Company", "Format", "Modules", "Shared", "Size", "Created By", "Date", ""].map((h, i) => (
                      <th key={i} style={{ padding: "9px 14px", textAlign: "left", fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", borderBottom: `1px solid ${BORDER}`, fontWeight: 600, letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {exports.map((e, i) => (
                    <tr key={i} style={{ borderBottom: i < exports.length - 1 ? `1px solid ${BORDER}` : "none" }}
                      onMouseEnter={el => el.currentTarget.style.background = SURFACE_2}
                      onMouseLeave={el => el.currentTarget.style.background = "transparent"}
                    >
                      <td style={{ padding: "11px 14px", color: TEXT_PRIMARY, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>{e.company}</td>
                      <td style={{ padding: "11px 14px" }}><Badge label={e.type} color={typeColor(e.type)} /></td>
                      <td style={{ padding: "11px 14px", color: TEXT_MUTED, fontSize: 11 }}>{e.modules.join(", ")}</td>
                      <td style={{ padding: "11px 14px" }}><Badge label={e.sharedWith.toUpperCase()} color={shareColor(e.sharedWith)} /></td>
                      <td style={{ padding: "11px 14px", color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{e.size}</td>
                      <td style={{ padding: "11px 14px", color: TEXT_MUTED }}>{e.createdBy}</td>
                      <td style={{ padding: "11px 14px", color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{e.ts}</td>
                      <td style={{ padding: "11px 14px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            data-testid={`export-download-${e.export_id || e.id}`}
                            onClick={() => handleDownloadExport(e)}
                            disabled={!e.export_id}
                            style={{
                              background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 5,
                              padding: "4px 10px", fontSize: 10, color: ACCENT,
                              cursor: e.export_id ? "pointer" : "not-allowed",
                              opacity: e.export_id ? 1 : 0.5,
                              fontFamily: "'DM Mono', monospace",
                            }}
                          >DOWNLOAD</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

        {/* BRIEF LIBRARY */}
        {activeTab === "briefs" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 4 }}>Brief Library</h2>
              <p style={{ fontSize: 13, color: TEXT_MUTED }}>All briefs you have exported across this workspace. Download a saved copy, or re-export the latest analysis.</p>
            </div>
            {exportsLoading ? (
              <div data-testid="briefs-loading" style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
                <span style={{ width: 22, height: 22, border: `2px solid ${ACCENT}33`, borderTopColor: ACCENT, borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : exports.length === 0 ? (
              <div data-testid="briefs-empty-state" style={{ textAlign: "center", padding: "80px 0", border: `1px dashed ${BORDER}`, borderRadius: 8 }}>
                <p style={{ fontSize: 15, color: TEXT_PRIMARY, marginBottom: 8 }}>No briefs exported yet.</p>
                <p style={{ fontSize: 13, color: TEXT_MUTED }}>Export a Brief from any analysis to build your library.</p>
              </div>
            ) : (
              <>
                {/* Filter bar — matches Saved Articles toolbar style */}
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    data-testid="briefs-search-input"
                    type="text"
                    value={briefSearch}
                    onChange={e => setBriefSearch(e.target.value)}
                    placeholder="Search by company..."
                    style={{
                      flex: "1 1 220px", maxWidth: 320,
                      background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 6,
                      padding: "7px 12px", fontSize: 12, color: TEXT_PRIMARY,
                      outline: "none", fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                  <div style={{ width: 1, height: 18, background: BORDER }} />
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {briefFormatOptions.map(f => (
                      <button
                        key={f}
                        data-testid={`briefs-format-filter-${f.toLowerCase()}`}
                        onClick={() => setBriefFormatFilter(f)}
                        style={{
                          fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
                          border: `1px solid ${briefFormatFilter === f ? ACCENT : BORDER}`,
                          background: briefFormatFilter === f ? `${ACCENT}18` : "transparent",
                          color: briefFormatFilter === f ? ACCENT : TEXT_MUTED,
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >{f === "All" ? "All Formats" : f}</button>
                    ))}
                  </div>
                  {briefFiltersActive && (
                    <button
                      data-testid="briefs-clear-filters-btn"
                      onClick={clearBriefFilters}
                      style={{
                        fontSize: 11, padding: "5px 11px", borderRadius: 6, cursor: "pointer",
                        border: `1px solid ${BORDER}`, background: "transparent", color: TEXT_DIM,
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >Clear filters ✕</button>
                  )}
                  <span style={{ marginLeft: "auto", fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
                    {filteredBriefs.length} {filteredBriefs.length === 1 ? "brief" : "briefs"}
                  </span>
                </div>

                {filteredBriefs.length === 0 ? (
                  <div data-testid="briefs-no-results" style={{ textAlign: "center", padding: "60px 0", border: `1px dashed ${BORDER}`, borderRadius: 8 }}>
                    <p style={{ fontSize: 14, color: TEXT_MUTED, marginBottom: 14 }}>No exports match your filters.</p>
                    <button
                      data-testid="briefs-no-results-clear-btn"
                      onClick={clearBriefFilters}
                      style={{
                        background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 6,
                        padding: "6px 14px", fontSize: 12, color: ACCENT, cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >Clear Filters</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {filteredBriefs.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`brief-card-${b.export_id || b.id}`}
                    style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY }}>{b.company}</span>
                        <Badge label={b.type.toUpperCase()} color={typeColor(b.type)} />
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {(b.modules || []).slice(0, 4).map(o => (
                          <span key={o} style={{ fontSize: 11, color: TEXT_MUTED, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 8px" }}>{o}</span>
                        ))}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 8 }}>Exported: {b.ts}</div>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        {b.export_id ? (
                          <button
                            data-testid={`brief-download-${b.export_id}`}
                            onClick={() => handleDownloadExport(b)}
                            style={{ background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "6px 12px", fontSize: 11, color: TEXT_MUTED, cursor: "pointer", fontFamily: "'DM Mono', monospace" }}
                          >DOWNLOAD</button>
                        ) : null}
                        {b.analysis_id ? (
                          <button
                            data-testid={`brief-reexport-${b.export_id || b.id}`}
                            onClick={() => handleReExport(b)}
                            style={{ background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`, borderRadius: 6, padding: "6px 12px", fontSize: 11, color: ACCENT, cursor: "pointer", fontFamily: "'DM Mono', monospace" }}
                          >RE-EXPORT →</button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                    ))}
                    {exportsHasMore && !briefFiltersActive && (
                      <div style={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
                        <button
                          data-testid="briefs-load-more-btn"
                          onClick={loadMoreExports}
                          disabled={exportsLoadingMore}
                          style={{
                            background: SURFACE_2,
                            border: `1px solid ${BORDER}`,
                            borderRadius: 8,
                            padding: "8px 18px",
                            fontSize: 12,
                            color: exportsLoadingMore ? TEXT_DIM : ACCENT,
                            cursor: exportsLoadingMore ? "wait" : "pointer",
                            fontFamily: "'DM Mono', monospace",
                            letterSpacing: "0.04em",
                          }}
                        >{exportsLoadingMore ? "Loading…" : "LOAD MORE"}</button>
                      </div>
                    )}
                    {exportsHasMore && briefFiltersActive && (
                      <div data-testid="briefs-load-more-hint" style={{ textAlign: "center", paddingTop: 6, fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
                        Showing {filteredBriefs.length} of {exports.length} loaded · clear filters to load more
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {showEditor && (
        <TemplateEditor
          template={editingTemplate}
          onClose={() => { setShowEditor(false); setEditingTemplate(null); }}
        />
      )}
    </div>
  );
}
